import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Truck, Battery, Wrench, Fuel, Package, Shield, MapPin, Clock, CreditCard, CheckCircle, Star, Phone, Mail, Navigation, Building2, Users } from 'lucide-react'

const serviceDetails: Record<string, {
  title: string
  icon: any
  description: string
  features: string[]
  howItWorks: string[]
  benefits: string[]
  pricing?: string
}> = {
  towing: {
    title: 'Towing Service',
    icon: Truck,
    description: '24/7 emergency towing service to transport your vehicle to your preferred garage or destination anywhere in Kenya.',
    features: [
      'Fast response time within 30 minutes',
      'Professional tow truck operators',
      'Real-time GPS tracking',
      'Safe vehicle handling',
      'Wide network coverage across Kenya',
      'Destination flexibility'
    ],
    howItWorks: [
      'Request towing through our app or website',
      'Provide your location and destination',
      'Get matched with nearby tower',
      'Track your vehicle in real-time',
      'Vehicle delivered safely to destination'
    ],
    benefits: [
      'Reliable and professional service',
      'Transparent pricing before service',
      'Live tracking for peace of mind',
      'Verified and insured operators',
      'Support for all vehicle types',
      'Emergency and scheduled towing'
    ],
    pricing: 'Based on distance and vehicle type. Prices start from KES 2,000 for short distances.'
  },
  battery: {
    title: 'Battery Assistance',
    icon: Battery,
    description: 'Jump start or battery replacement services to get you back on the road quickly.',
    features: [
      'Quick jump-start service',
      'Battery testing and diagnosis',
      'Battery replacement if needed',
      'Quality batteries from trusted brands',
      'Mobile service at your location',
      'Warranty on replacement batteries'
    ],
    howItWorks: [
      'Request battery assistance',
      'Operator assesses your battery',
      'Jump start or replacement as needed',
      'Payment processed securely',
      'You\'re back on the road'
    ],
    benefits: [
      'Fast response within 30 minutes',
      'Professional diagnosis',
      'Quality replacement parts',
      'Mobile convenience',
      'Competitive pricing',
      'Service guarantee'
    ],
    pricing: 'Jump start: KES 1,500. Battery replacement: varies by vehicle type.'
  },
  'flat-tyre': {
    title: 'Flat Tyre Help',
    icon: Wrench,
    description: 'Quick tyre change or repair services wherever you are in Kenya.',
    features: [
      'Mobile tyre change service',
      'Tyre repair when possible',
      'Spare tyre installation',
      'Puncture repair services',
      '24/7 emergency response',
      'All vehicle types supported'
    ],
    howItWorks: [
      'Request flat tyre assistance',
      'Operator arrives at your location',
      'Tyre assessed for repair or change',
      'Quick repair or replacement done',
      'You\'re back on the road safely'
    ],
    benefits: [
      'Fast and convenient service',
      'Professional tyre technicians',
      'Quality workmanship',
      'Competitive pricing',
      'Mobile service at your location',
      'Safety guarantee'
    ],
    pricing: 'Tyre change: KES 800. Repair: varies by damage extent.'
  },
  fuel: {
    title: 'Fuel Delivery',
    icon: Fuel,
    description: 'Emergency fuel delivery to get you back on the road when you run out of fuel.',
    features: [
      '24/7 emergency fuel delivery',
      'Multiple fuel types (Petrol, Diesel, Kerosene)',
      'Nearby fuel station partners',
      'Flexible quantity options',
      'Transparent pricing',
      'Quick delivery within 30 minutes'
    ],
    howItWorks: [
      'Request fuel delivery with quantity',
      'Select nearby fuel station',
      'Fuel delivered to your location',
      'Payment via M-Pesa',
      'You\'re ready to continue your journey'
    ],
    benefits: [
      'No need to walk to station',
      'Emergency response anytime',
      'Verified fuel station partners',
      'Transparent pricing',
      'Multiple payment options',
      'Reliable service'
    ],
    pricing: 'Fuel price per liter + delivery fee. Delivery fee varies by distance (KES 500-1,000).'
  },
  'spare-parts': {
    title: 'Spare Parts',
    icon: Package,
    description: 'Order genuine vehicle parts from verified vendors across Kenya.',
    features: [
      'Wide range of vehicle parts',
      'Verified and trusted vendors',
      'Genuine OEM parts',
      'Quality aftermarket options',
      'Doorstep delivery',
      'Warranty on parts'
    ],
    howItWorks: [
      'Browse or search for parts',
      'Select parts and quantity',
      'Add to cart and checkout',
      'Payment via M-Pesa',
      'Parts delivered to your location'
    ],
    benefits: [
      'Access to genuine parts',
      'Verified vendors',
      'Competitive pricing',
      'Doorstep delivery',
      'Quality guarantee',
      'Wide selection'
    ],
    pricing: 'Prices vary by part and vendor. Discounts available for bulk orders.'
  },
  'insurance': {
    title: 'Insurance Claims',
    icon: Shield,
    description: 'Hassle-free insurance claim processing for roadside assistance services.',
    features: [
      'Direct insurance integration',
      'Digital claim submission',
      'Fast claim processing',
      'Paperless documentation',
      'Real-time claim tracking',
      'Partners with major insurers'
    ],
    howItWorks: [
      'Service is automatically logged',
      'Claim submitted to your insurer',
      'Insurer reviews and approves',
      'Payment processed directly',
      'You receive service without upfront cost'
    ],
    benefits: [
      'No upfront payment needed',
      'Fast claim processing',
      'Digital documentation',
      'Direct insurer communication',
      'Transparent process',
      'Partnered with major insurers'
    ],
    pricing: 'Covered by your insurance policy. Deductibles may apply.'
  },
  'tower-operators': {
    title: 'Tower Operators',
    icon: Truck,
    description: 'Join our network of professional tow truck operators and grow your business.',
    features: [
      'Access to customer requests',
      'Flexible work schedule',
      'Competitive commission rates',
      'Real-time job notifications',
      'GPS tracking system',
      'Payment integration'
    ],
    howItWorks: [
      'Register as a tower operator',
      'Complete verification process',
      'Receive job requests',
      'Accept and complete jobs',
      'Get paid automatically'
    ],
    benefits: [
      'Steady stream of customers',
      'Flexible working hours',
      'Fair commission structure',
      'Real-time job matching',
      'Support and training',
      'Grow your business'
    ],
    pricing: 'Competitive commission rates on completed jobs. No signup fees.'
  },
  garages: {
    title: 'Garages',
    icon: Building2,
    description: 'Partner with us as a destination garage and receive more vehicle service requests.',
    features: [
      'Receive vehicle service requests',
      'Verified partner status',
      'Priority customer referrals',
      'Integrated payment system',
      'Service tracking',
      'Marketing exposure'
    ],
    howItWorks: [
      'Register your garage',
      'Complete verification',
      'Get listed as destination',
      'Receive service requests',
      'Complete services and get paid'
    ],
    benefits: [
      'Increased customer base',
      'Verified partner badge',
      'Priority in job matching',
      'Integrated payments',
      'Marketing support',
      'Business growth'
    ],
    pricing: 'No listing fees. Commission on completed service jobs.'
  },
  'parts-vendors': {
    title: 'Parts Vendors',
    icon: Package,
    description: 'Sell your vehicle parts to thousands of customers through our platform.',
    features: [
      'Wide customer reach',
      'Digital storefront',
      'Inventory management',
      'Order management',
      'Payment integration',
      'Delivery coordination'
    ],
    howItWorks: [
      'Register as parts vendor',
      'List your parts inventory',
      'Receive customer orders',
      'Process and deliver orders',
      'Get paid securely'
    ],
    benefits: [
      'Access to large customer base',
      'Digital inventory management',
      'Secure payment processing',
      'Delivery coordination',
      'Marketing exposure',
      'Analytics dashboard'
    ],
    pricing: 'Small commission on sales. No inventory listing fees.'
  },
  'insurance-providers': {
    title: 'Insurance Providers',
    icon: Shield,
    description: 'Partner with us to provide seamless insurance claim processing for your customers.',
    features: [
      'Direct claim integration',
      'Digital claim processing',
      'Real-time claim tracking',
      'Automated documentation',
      'Fast approval workflow',
      'Reduced claim processing time'
    ],
    howItWorks: [
      'Partner with Mkononi',
      'Integrate claim systems',
      'Receive digital claims',
      'Review and approve claims',
      'Process payments digitally'
    ],
    benefits: [
      'Streamlined claim process',
      'Reduced paperwork',
      'Faster claim settlement',
      'Digital documentation',
      'Improved customer experience',
      'Cost reduction'
    ],
    pricing: 'Contact us for partnership pricing. Volume-based discounts available.'
  }
}

export default function ServiceDetailPage() {
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(window.location.search)
  const type = urlParams.get('type') || 'towing'

  const detail = serviceDetails[type] || serviceDetails.towing
  const Icon = detail.icon

  const isPartner = ['tower-operators', 'garages', 'parts-vendors', 'insurance-providers'].includes(type)

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        padding: '1rem 2rem',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.5rem 1rem',
              background: '#fef2f2',
              border: '1px solid #dc2626',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              color: '#dc2626',
              fontWeight: 600,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--red)' }}>Mk</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)' }}>ononi</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        {/* Title Section */}
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'var(--red-light)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icon size={40} style={{ color: 'var(--red)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 800,
              color: 'var(--text)',
              marginBottom: '0.5rem'
            }}>
              {detail.title}
            </h1>
            <p style={{ fontSize: '1rem', color: 'var(--muted)', lineHeight: 1.6 }}>
              {detail.description}
            </p>
          </div>
        </div>

        {/* Pricing */}
        {detail.pricing && (
          <div style={{
            background: 'var(--green-light)',
            padding: '1.5rem',
            borderRadius: 'var(--radius)',
            marginBottom: '2rem',
            border: '1px solid ' + 'var(--green)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <CreditCard size={24} style={{ color: 'var(--green)' }} />
            <div>
              <div style={{ fontSize: '0.85rem', color: 'var(--green)', fontWeight: 600, marginBottom: '0.25rem' }}>
                Pricing
              </div>
              <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 600 }}>
                {detail.pricing}
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Star size={20} style={{ color: 'var(--red)' }} />
            Key Features
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {detail.features.map((feature, index) => (
              <li key={index} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                marginBottom: '1rem',
                fontSize: '1rem',
                color: 'var(--text)'
              }}>
                <CheckCircle size={20} style={{ color: 'var(--green)', flexShrink: 0, marginTop: '0.2rem' }} />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* How It Works */}
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Navigation size={20} style={{ color: 'var(--red)' }} />
            How It Works
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {detail.howItWorks.map((step, index) => (
              <div key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'var(--red)',
                  color: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  fontWeight: 700,
                  flexShrink: 0
                }}>
                  {index + 1}
                </div>
                <p style={{ fontSize: '1rem', color: 'var(--text)', lineHeight: 1.6 }}>
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <CheckCircle size={20} style={{ color: 'var(--green)' }} />
            Benefits
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {detail.benefits.map((benefit, index) => (
              <li key={index} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                marginBottom: '1rem',
                fontSize: '1rem',
                color: 'var(--text)'
              }}>
                <CheckCircle size={20} style={{ color: 'var(--green)', flexShrink: 0, marginTop: '0.2rem' }} />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div style={{
          background: 'var(--red)',
          padding: '2rem',
          borderRadius: 'var(--radius)',
          textAlign: 'center',
          color: 'white'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>
            {isPartner ? 'Ready to Partner with Us?' : 'Need This Service?'}
          </h2>
          <p style={{ fontSize: '1rem', marginBottom: '1.5rem', opacity: 0.9 }}>
            {isPartner ? 'Join our network and grow your business today' : 'Get help now with just a few taps'}
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '1rem 2rem',
              background: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontWeight: 700,
              color: 'var(--red)',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {isPartner ? 'Partner with Us' : 'Get Started'}
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  )
}
